import { useCallback, useEffect, useMemo, useState } from "react";
import InstanceApi from "../../api/instance/InstanceApi";

// Components
import ConditionalRender from "../../helpers/ConditionalRender";
import { Dropdown } from "../../components/forms";
import { CHART_TYPE_OPTIONS } from "../../component/Constants";
import Alert from "../../component/Alert";
import InstanceComparisonOptions from "./instance-comparison-options";

// Types
import { InstanceOptions } from "./statistics-comparison/types";
import { ActivityComparisonOptionsCardProps } from "./activity-comparison/types";

// Constants
import {
    INTERVAL_FIFTEEN_MINUTES,
    INTERVAL_FIVE_MINUTES,
    INTERVAL_MINUTE,
    INTERVAL_ONE_DAY,
    INTERVAL_ONE_HOUR
} from "../../component/Constants";
import { ERROR_MESSAGE_DATES } from "./statistics-comparison/constants";
import { getDefaultPeriodDates, getTimeZone } from "../../helpers/utils";

const ComparisonOptionsCard = (props: ActivityComparisonOptionsCardProps) => {
    const [firstInstanceOptions, setFirstInstanceOptions] = useState<InstanceOptions>(props.options.firstInstanceOptions)
    const [secondInstanceOptions, setSecondInstanceOptions] = useState<InstanceOptions>(props.options.secondInstanceOptions)
    const [metricsList, setMetricsList] = useState<string[]>([])
    const title = props.type === 'activity' ? 'Activity comparison options' : 'SQL Stats comparison options'

    const allRequiredOptionsSet = useCallback(() => {
        return !!(props.options.aggregate &&
            props.options.chart &&
            props.options.metric &&
            props.options.topN &&
            firstInstanceOptions.instanceId > -1 &&
            firstInstanceOptions.periodEnd &&
            firstInstanceOptions.periodStart &&
            secondInstanceOptions.instanceId > -1 &&
            secondInstanceOptions.periodStart &&
            secondInstanceOptions.periodEnd && validDates)
    }, [props.options, firstInstanceOptions, secondInstanceOptions])

    useEffect(() => {
        if (firstInstanceOptions.instanceId !== -1 && firstInstanceOptions.periodEnd && firstInstanceOptions.periodStart) {
            props.onOptionsChange('firstInstanceOptions', firstInstanceOptions)
        }

        if (secondInstanceOptions.instanceId === -1) {
            setSecondInstanceOptions({
                ...secondInstanceOptions,
                instanceId: firstInstanceOptions.instanceId,
                instanceName: firstInstanceOptions.instanceName,
                instanceType: firstInstanceOptions.instanceType
            })
        }
    }, [firstInstanceOptions, props.options.firstInstanceOptions])

    const resetFilters = () => {
        props.resetFilters()
        const defaults = getDefaultPeriodDates(getTimeZone())
        setSecondInstanceOptions({
            instanceId: -1,
            instanceType: undefined,
            periodStart: defaults.secondPeriodStart,
            periodEnd: defaults.secondPeriodEnd,
            instanceName: '',
            snapshotId: NaN,
        })
        setFirstInstanceOptions({
            instanceId: -1,
            instanceType: undefined,
            periodStart: defaults.firstPeriodStart,
            periodEnd: defaults.firstPeriodEnd,
            instanceName: '',
            snapshotId: NaN,
        })
    }

    useEffect(() => {
        if (secondInstanceOptions.instanceId !== -1 && secondInstanceOptions.periodEnd && secondInstanceOptions.periodStart) {
            props.onOptionsChange('secondInstanceOptions', secondInstanceOptions)
        }
    }, [secondInstanceOptions])

    const showSqlIdToggle = useMemo(() => {
        return firstInstanceOptions.instanceType === 'oracle' || secondInstanceOptions.instanceType === 'oracle';
    }, [firstInstanceOptions, secondInstanceOptions])

    useEffect(() => {
        const instanceTypes = [firstInstanceOptions.instanceType, secondInstanceOptions.instanceType]
        let _metricsList: any[] = []
        instanceTypes.forEach((instanceType: string | undefined) => {
            if (instanceType && props.type === 'sql-activity') {
                InstanceApi.getAvailableMetrics(`/sql/statistic/name?type=${instanceType}`).then(metrics => {
                    _metricsList.push(metrics)

                    let filteredMetrics = []
                    if (_metricsList.length > 1) {
                        filteredMetrics = _metricsList[0].filter((value: string) => _metricsList[1].includes(value));
                    }
                    setMetricsList(filteredMetrics)
                })
            }
        })
    }, [firstInstanceOptions, secondInstanceOptions])

    const normalizeDate = (date: Date) => new Date(date?.setMilliseconds(0));
    const validDates = useMemo(() => {
        const normalizedStart1 = normalizeDate(firstInstanceOptions.periodStart);
        const normalizedEnd1 = normalizeDate(firstInstanceOptions.periodEnd);
        const normalizedStart2 = normalizeDate(secondInstanceOptions.periodStart);
        const normalizedEnd2 = normalizeDate(secondInstanceOptions.periodEnd);

        return normalizedStart1 < normalizedEnd1 && normalizedStart2 < normalizedEnd2;
    }, [firstInstanceOptions, secondInstanceOptions]);

    const setTopN = (val: any) => {
        const result = Math.abs(parseInt(val.target.value, 10))
        props.onOptionsChange('topN', result > 100 ? 100 : result)
    }

    return <div className="card collapsible">
        <div className="card-header">
            <i className="fal fa-stopwatch fa-fw" aria-hidden="true"/>
            {title}
            <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseSettings"
               aria-expanded="false" aria-controls="collapseSettings"/>
        </div>
        <div id="collapseSettings" className="card-body collapse show">
            <ConditionalRender if={!validDates}>
                <Alert message={ERROR_MESSAGE_DATES.INVALID.TEXT} heading={ERROR_MESSAGE_DATES.INVALID.TITLE}
                       variant="alert-danger"/>
            </ConditionalRender>
            <div className='row row-cols-lg-12'>

                <InstanceComparisonOptions instanceKey='firstInstanceOptions'
                                           loading={props.loading}
                                           instances={props.instances}
                                           {...firstInstanceOptions}
                                           onOptionsChange={setFirstInstanceOptions}/>

                <InstanceComparisonOptions instanceKey='secondInstanceOptions'
                                           loading={props.loading}
                                           {...secondInstanceOptions}
                                           instances={props.instances}
                                           onOptionsChange={setSecondInstanceOptions}/>
            </div>
            <div className="d-flex flex-wrap align-items-end gap-3 mt-3 filter-options-fields comparison-options-fields">
                <ConditionalRender if={props.type === 'activity'}>
                    <div className="filter-field-wide">
                        <span className="required">
                            <label>Compare</label>
                        </span>
                        <Dropdown
                            ariaLabel="Select Aggregate"
                            disabled={true}
                            value={props.options.compare}
                            onChange={(value) => props.onOptionsChange('compare', String(value))}
                            options={[
                                { value: 'sql', label: 'SQL Statements' },
                                { value: 'grouped', label: 'Grouped SQL Statements' },
                                { value: 'programs', label: 'Programs' },
                                { value: 'users', label: 'Users' },
                                { value: 'clients', label: 'Clients' },
                                { value: 'databases', label: 'Databases' }
                            ]}
                        />
                    </div>
                </ConditionalRender>

                <ConditionalRender if={props.type === 'sql-activity'}>
                    <div className="filter-field-wide">
                        <span className="required">
                            <label>Metric</label>
                        </span>
                        <Dropdown
                            value={props.options.metric || ""}
                            disabled={props.loading}
                            ariaLabel="Select Metric"
                            placeholder="Select metric"
                            onChange={(value) => props.onOptionsChange('metric', String(value))}
                            options={metricsList.map((metric: string) => ({ value: metric, label: metric }))}
                        />
                    </div>
                </ConditionalRender>
                <div className="filter-field-narrow">
                    <label>Interval</label>
                    <Dropdown
                        ariaLabel="Select Aggregate"
                        disabled={props.loading}
                        value={props.options.aggregate}
                        onChange={(value) => props.onOptionsChange('aggregate', String(value))}
                        options={[
                            { value: 'auto', label: 'Auto' },
                            { value: 'timeslice', label: 'Timeslice' },
                            { value: INTERVAL_MINUTE, label: 'Minute' },
                            { value: INTERVAL_FIVE_MINUTES, label: '5 Minutes' },
                            { value: INTERVAL_FIFTEEN_MINUTES, label: '15 Minutes' },
                            { value: INTERVAL_ONE_HOUR, label: 'Hour' },
                            { value: INTERVAL_ONE_DAY, label: 'Day' }
                        ]}
                    />
                </div>
                <div className="filter-field-wide">
                    <label>Chart</label>
                    <Dropdown
                        value={props.options.chart}
                        disabled={props.loading}
                        ariaLabel="Select Chart Type"
                        onChange={(value) => props.onOptionsChange('chart', String(value))}
                        options={CHART_TYPE_OPTIONS}
                    />
                </div>
                <div className="filter-field-narrow">
                    <span className="required text-nowrap">
                        <label>Top N</label>
                    </span>
                    <input className="form-control"
                           type='number'
                           max={100}
                           min={1}
                           value={props.options.topN || ''}
                           disabled={props.loading}
                           onChange={(e) => {
                               setTopN(e)
                           }}
                           aria-label="Select Top N Statement to Show"/>
                </div>
                <div className="filter-field-wide d-none">
                    <label>Tag</label>
                    <Dropdown
                        value={props.options.tagName}
                        disabled={props.loading}
                        ariaLabel="Select Tag"
                        onChange={(value) => props.onOptionsChange('tagName', String(value))}
                        options={[{ value: 'none', label: 'None' }]}
                    />
                </div>
                <div>
                    <label htmlFor="other-switch" data-tooltip-id="global-tooltip"
                           data-tooltip-content='All other statements combined'>Other</label>
                    <div className='form-check form-switch'>
                        <input
                            type="checkbox"
                            id="other-switch"
                            disabled={props.loading}
                            className="form-check-input"
                            onChange={() => {
                                props.onOptionsChange('other', !props.options.other)
                            }}
                            checked={props.options.other}
                        />
                    </div>
                </div>
                <ConditionalRender if={showSqlIdToggle}>
                    <div>
                        <label htmlFor="show-sql-id-switch" className='text-nowrap'>Show SQL IDs</label>
                        <div className='form-check form-switch'>
                            <input
                                type="checkbox"
                                id="show-sql-id-switch"
                                disabled={props.loading}
                                className="form-check-input"
                                onChange={() => {
                                    props.onOptionsChange('showSqlId', !props.options.showSqlId)
                                }}
                                checked={props.options.showSqlId}
                            />
                        </div>
                    </div>
                </ConditionalRender>
                <div className="filter-field-narrow">
                    <label
                        className='text-nowrap'>{`${props.options.showSqlId ? 'SQL ID' : 'SQL #'}`}</label>
                    <input className="form-control"
                           type='text'
                           max={100}
                           min={1}
                           value={props.options.customSqlId}
                           disabled={props.loading}
                           onChange={(e) => {
                               props.onOptionsChange('customSqlId', e.target.value)
                           }}/>
                </div>
                <div className="d-flex align-items-end ms-auto">
                    <button className="btn btn-dark mx-2 text-nowrap"
                            disabled={props.loading}
                            onClick={() => resetFilters()}>
                        <i className="far fa-undo fa-fw fa-sm"/><span>Reset</span>
                    </button>

                    <button className="btn btn-success text-nowrap"
                            disabled={props.loading || !allRequiredOptionsSet()}
                            onClick={() => props.onGenerate()}>
                        <i className="fal fa-play fa-fw fa-sm"/><span>Generate</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
}

export default ComparisonOptionsCard;
